/** Concise dashboard metric descriptions (overview / products / marketing). */

const orderConcise: Record<string, string> = {
  total_sales:
    'Subtotal from matched orders (line prices minus discounts). Excludes shipping and tax. Selected date range.',
  net_sales: 'Net sales after refunds on matched orders in the selected date range.',
  total_orders: 'Number of matched orders in the selected date range.',
  total_revenue:
    'Total collected revenue (sales + shipping + tax) from matched orders in the selected date range.',
  total_items_sold: 'Units sold across matched orders in the selected date range.',
  total_tax: 'Total tax collected on matched orders, including shipping-related tax.',
  total_store_fees: 'Store/platform fees on matched orders in the selected date range.',
  total_marketplace_fees: 'Marketplace fees on matched orders in the selected date range.',
  total_shippings:
    'Shipping charges collected on matched orders (excludes shipping tax). Selected date range.',
  total_costs: 'Sum of product costs on matched orders in the selected date range.',
  total_cost_of_goods_sold: 'Cost of goods sold after refunds on matched orders.',
  total_discounts: 'Discounts applied on matched orders in the selected date range.',
  total_sale_discount: 'Sale-price discounts on matched orders in the selected date range.',
  total_profit:
    'Profit on matched orders: subtotal minus costs, taxes, fees, gateway fees, plus shipping profit adjustments.',
  total_gross_sales: 'Gross sales (subtotal sales + shipping collected) on matched orders.',
  total_refunds: 'Refund amounts on matched orders in the selected date range.',
  total_items_refunded: 'Units refunded on matched orders in the selected date range.',
  total_cost_of_refunds: 'Product cost attributed to refunded items on matched orders.',
  gross_margin:
    'Gross margin % on matched orders: gross profit vs net sales (after refunds).',
  total_customers:
    'Distinct customers who placed matched orders in the selected date range (includes guest checkouts).',
  avg_price: 'Average selling price per unit across matched order lines.',
  avg_shipping: 'Average shipping collected per matched order.',
  avg_tax: 'Average tax per matched order.',
  avg_order: 'Average order subtotal per matched order (excludes shipping and tax).',
  avg_items_per_order: 'Average units sold per matched order.',
  max_sales: 'Largest order subtotal among matched orders in the range.',
  min_sales: 'Smallest order subtotal among matched orders in the range.',
  max_tax: 'Largest total tax on a single matched order in the range.',
  min_tax: 'Smallest total tax on a single matched order in the range.',
  max_shipping: 'Largest shipping collected on a single matched order in the range.',
  min_shipping: 'Smallest shipping collected on a single matched order in the range.',
  max_discount: 'Largest discount amount on a single matched order in the range.',
  min_discount: 'Smallest discount amount on a single matched order in the range.',
  total_shipment_costs: 'Total shipping label costs for matched shipments.',
  total_shipment_profit: 'Shipping collected minus label cost for matched orders.',
  avg_shipment_profit: 'Average shipping profit per matched order.',
  total_shipment_insurance_costs: 'Insurance fees paid on shipping labels.',
  total_shipment_insurance_value: 'Declared insurance value on shipments.',
  tax_rate: 'Effective tax rate: total tax divided by total sales (matched orders).',
  gross_tax_rate: 'Tax divided by gross sales (sales + shipping) on matched orders.',
  avg_fulfillment_time: 'Average time from order created to fulfilled (matched orders).',
  min_fulfillment_time: 'Shortest fulfillment time in the selected date range.',
  max_fulfillment_time: 'Longest fulfillment time in the selected date range.',
  total_taxed_orders: 'Matched orders with tax greater than zero.',
  total_discounted_orders: 'Matched orders that used a discount.',
  total_shipping_tax: 'Tax specifically on shipping charges for matched orders.',
  total_store_credit_amount: 'Store credit applied on matched orders.',
  total_store_credit_refund: 'Store credit refunded on matched orders.',
  total_refund_tax: 'Sales tax refunded with refunds on matched orders.',
  total_shipping_refund: 'Shipping amounts refunded on matched orders.',
  gateway_fee: 'Payment gateway fees on matched orders.',
  backcharges_cost: 'Carrier backcharges on shipping labels.',
  points_earned: 'Loyalty points earned on matched orders.',
  points_claimed: 'Loyalty points redeemed on matched orders.',
  points_refunded: 'Loyalty points refunded with returns.',
  total_points_net_claimed: 'Net loyalty points redeemed after refunds.',
  total_points_claimed_and_refunded: 'Points redeemed plus points refunded (absolute movement).',
  gift_card_amount: 'Gift card value redeemed on matched orders.',
  total_process_fee_refund: 'Payment processor fee amounts refunded.',
}

const productConcise: Record<string, string> = {
  default_sales:
    'Sales at default/catalog price × quantity for matched sold product rows (selected date range).',
  total_sales:
    'Subtotal from matched sold product lines (price × qty minus discounts). Excludes shipping and tax.',
  total_orders: 'Orders that include at least one matched sold product line.',
  total_items_sold: 'Units sold across matched product rows.',
  total_products: 'Distinct products with sales in matched product rows.',
  total_product_taxes: 'Line-item tax on matched sold products.',
  total_product_discounts: 'Line-item discounts on matched sold products.',
  total_sale_discount: 'Sale-price discounts on matched sold products.',
  total_product_costs: 'Product cost (cost × qty) on matched sold lines.',
  total_product_profit:
    'Line profit on matched sold products (after discounts, fees, tax, and cost).',
  total_refunds: 'Refunds attributed to matched product rows.',
  total_items_refunded: 'Units refunded on matched product rows.',
  total_cost_of_refunds: 'Product cost of refunded units on matched rows.',
  total_cost_of_goods_sold: 'COGS after refunds on matched product rows.',
  gross_margin: 'Gross margin % rolled up from matched product rows.',
  avg_price: 'Average selling price per unit on matched product lines.',
  avg_items_per_order: 'Average units per order among orders with matched product sales.',
  avg_item_tax: 'Average tax per unit on matched product lines.',
  avg_item_cost: 'Average product cost per unit on matched lines.',
  avg_item_discount: 'Average discount per unit on matched lines.',
  max_item_sales: 'Highest line sales amount in matched product rows.',
  min_item_sales: 'Lowest line sales amount in matched product rows.',
  max_items_sold: 'Highest quantity sold on a single matched product line.',
  min_items_sold: 'Lowest quantity sold on a single matched product line.',
  max_item_tax: 'Highest line tax in matched product rows.',
  min_item_tax: 'Lowest line tax in matched product rows.',
  max_item_cost: 'Highest line cost in matched product rows.',
  min_item_cost: 'Lowest line cost in matched product rows.',
  max_item_discount: 'Highest line discount in matched product rows.',
  min_item_discount: 'Lowest line discount in matched product rows.',
  total_marketplace_fees: 'Marketplace fees on matched product rows.',
}

const marketingConcise: Record<string, string> = {
  utm_orders: 'Orders attributed to UTM campaigns (matched) in the selected date range.',
  utm_sales:
    'Subtotal from UTM-attributed orders (line prices minus discounts). Excludes shipping and tax.',
  utm_items_sold: 'Units sold on UTM-attributed orders.',
  utm_avg_price: 'Average item price on UTM-attributed order lines.',
  utm_avg_order: 'Average order subtotal for UTM-attributed orders (excludes shipping and tax).',
  utm_avg_items_per_order: 'Average units per UTM-attributed order.',
  utm_taxes: 'Total tax on UTM-attributed orders.',
  utm_shippings: 'Shipping collected on UTM-attributed orders (excludes shipping tax).',
  utm_discounts: 'Discounts on UTM-attributed orders.',
  utm_profit: 'Profit on UTM-attributed orders (PayHelm profit definition).',
  utm_refunds: 'Refunds on UTM-attributed orders.',
  utm_gross_margin: 'Gross margin % on UTM-attributed orders.',
  utm_customers: 'Distinct customers on UTM-attributed orders (includes guests).',
  avg_shipping: 'Average shipping collected per UTM-attributed order.',
  avg_tax: 'Average tax per UTM-attributed order.',
  max_sales: 'Largest order subtotal among UTM-attributed orders.',
  min_sales: 'Smallest order subtotal among UTM-attributed orders.',
  max_tax: 'Largest total tax on a single UTM-attributed order.',
  min_tax: 'Smallest total tax on a single UTM-attributed order.',
  max_shipping: 'Largest shipping on a single UTM-attributed order.',
  min_shipping: 'Smallest shipping on a single UTM-attributed order.',
  max_discount: 'Largest discount on a single UTM-attributed order.',
  min_discount: 'Smallest discount on a single UTM-attributed order.',
}

const legacyPrefixContent = {
  total: 'Total',
  avg: 'Average',
  max: 'The maximum',
  min: 'The minimum',
}

const legacyMainContent = {
  sale: "subtotal amount (all line items' price - discount, excluding shipping and tax) of",
  product_sale: 'sales amount (individual item price x quantity - discount ) off',
  default_sales: 'default sales amount (default item price x quantity sold) of',
  order: 'number of',
  taxed_order: 'number of',
  discounted_order: 'number of',
  product_order: 'number of orders that contain',
  items_sold: 'items sold in',
  items_per_order: 'quantity of sold items per order in',
  price: 'individual item price in',
  product_items_sold: 'sold items of',
  tax: 'tax amount (including shipping tax) of',
  shipping: 'shipping cost amount (excluding shipping tax) of',
  store_fee: 'store fee amount of',
  marketplace_fee: 'marketplace fees amount of',
  cost: 'product cost (individual item cost x quantity) amount of',
  discount: 'discount amount of',
  profit:
    'profit amount (subtotal - total product cost - total tax - store fee - marketplace fee - gateway fee + (total shipping - total shipping label cost - total shipping backcharges)) of',
  gross_sale: 'gross sale (total sales + shipping cost) amount of',
  refund: 'refund amount of',
  product: 'number of distinct results in',
  product_taxe: 'amount of line item tax of',
  product_discount: 'amount of line item discount of',
  product_cost: 'amount of line item cost (individual item cost x quantity) of',
  product_profit:
    'amount of line item profit (individual item price x quantity - discount - total marketplace fees - total product tax - total product cost) of',
  item_tax: 'amount of line item tax of',
  item_cost: 'amount of line item cost (individual item cost x quantity) of',
  item_discount: 'amount of line item discount of',
  item_sale: 'amount of line item sales (individual item price x quantity - discount) of',
  revenue: 'revenue (total tax + total shipping + total sales of',
  gross_margin:
    "percentage of gross profit (net sales - cost) out of net sales (all line items' price - discount - refund) of",
  shipment_cost: 'amount of shipping label cost of',
  shipment_insurance_cost: 'amount of shipping insurance cost of',
  shipment_insurance_value: 'declared value of the shipment to insure of',
  shipment_profit: 'shipping profit (shipping cost - shipping label cost) of',
  customer: 'unique customers in the date range including guest checkouts of',
  tax_rate: 'tax rate (total tax / total sales) of',
  gross_tax_rate: 'gross tax rate (total tax / total gross sales) of',
  fulfillment_time: 'fulfillment time (order completed - order created) of',
  net_sales: 'sales - total sales of matched refunded orders',
  cost_of_refund: 'product cost of refunded items in',
  cost_of_goods_sold: 'product cost - refunded product cost of',
  items_refunded: 'number of items refunded in',
  sale_discount: 'sale discount (base price - sale price + discounts) of',
  shipping_tax: 'shipping tax amount of',
  store_credit_amount: 'amount of store credit redeemed of',
  store_credit_refund: 'amount of store credit refunded of',
  refund_tax: 'amount of sales tax refunded of',
  shipping_refund: 'amount of shipping refunded of',
  gateway_fee: 'amount of gateway fees of',
  backcharges_cost: 'amount of shipping backcharges cost of',
  points_claimed: 'amount of points claimed of',
  points_refunded: 'amount of points refunded of',
  points_earned: 'amount of points earned of',
  points_claimed_and_refunded: 'amount of points claimed and refunded of',
  points_net_claimed: 'amount of net points claimed of',
  gift_card_amount: 'amount of gift card redeemed of',
}

const legacySuffixContent = {
  order: 'matched orders',
  product: 'matched sold products',
  marketing: 'matched UTM orders',
}

const legacyCustomSuffix = {
  taxed_order: 'that have taxes paid',
  discounted_order: 'that have discounts',
}

function legacyGetMetricTooltipContent(type: string, metricName: string) {
  const [prefix, ...tokens] = metricName
    .replace('utm_avg_', 'avg_')
    .replace('utm_', 'total_')
    .replace('_product_', '_')
    .split('_')
  const name = [
    'gross_margin',
    'tax_rate',
    'gross_tax_rate',
    'net_sales',
    'default_sales',
    'gateway_fee',
    'backcharges_cost',
    'points_earned',
    'points_claimed',
    'points_refunded',
    'gift_card_amount',
  ].includes(metricName)
    ? metricName
    : tokens.join('_').replace(/s$/, '')

  return [
    legacyPrefixContent[prefix] || 'Total',
    legacyMainContent[`${type}_${name}`] || legacyMainContent[name],
    legacySuffixContent[type],
    legacyCustomSuffix[name],
  ]
    .filter((t) => t)
    .join(' ')
}

export const getMetricTooltipContent = (type: string, metricName: string) => {
  if (type === 'order') {
    const text = orderConcise[metricName]
    if (text) return text
  }
  if (type === 'product') {
    const text = productConcise[metricName]
    if (text) return text
  }
  if (type === 'marketing') {
    const text = marketingConcise[metricName]
    if (text) return text
  }
  return legacyGetMetricTooltipContent(type, metricName)
}
